"use client";

import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import i18n, { isSupportedLang, type SupportedLang } from "@/lib/i18n";

type LocaleContextValue = {
  lang: SupportedLang;
};

const LocaleContext = createContext<LocaleContextValue>({ lang: "cs" });

function resolveLang(searchParamLang: string | null): SupportedLang {
  if (isSupportedLang(searchParamLang)) return searchParamLang;

  if (typeof window !== "undefined") {
    try {
      const stored = sessionStorage.getItem("praetor:lang");
      if (isSupportedLang(stored)) return stored;
    } catch { /* sessionStorage unavailable */ }
  }

  return "cs";
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const lang = resolveLang(searchParams.get("loc"));

  // Persist to sessionStorage
  if (typeof window !== "undefined") {
    try {
      sessionStorage.setItem("praetor:lang", lang);
    } catch { /* sessionStorage unavailable */ }
  }

  // Keep i18next in sync with the resolved language
  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  return (
    <LocaleContext.Provider value={{ lang }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}
